import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Download, ExternalLink, Share2, X } from "lucide-react";
import type * as maplibregl from "maplibre-gl";
import { ACTIVITY_SHARE_LAYOUT, type ActivityShareData, type ActivityShareFormat } from "@atlas/shared";
import { Button } from "./Button";
import { ActivityRouteMap } from "./map/ActivityRouteMap";
import { activityShareImageUrl, fetchActivityShare, fetchActivityShareUrl } from "../api/activities";

// Share an activity: mints the signed public link and previews/downloads the
// share images. Three looks:
//   canvas      - route + stats on the Atlas ink canvas (server PNG)
//   transparent - overlay-only PNG for stories (server PNG)
//   map         - live MapLibre tiles under the route, composited in the
//                 browser with the server's stats-only transparent overlay
// The identity toggle changes the minted link (it's folded into the sig), so
// flipping it re-mints; the preview and copied link always agree.

type Variant = "canvas" | "transparent" | "light" | "map";

const FORMATS: { value: ActivityShareFormat; label: string; hint: string }[] = [
  { value: "story", label: "Story", hint: "1080 x 1920" },
  { value: "feed", label: "Feed", hint: "1080 x 1350" },
  { value: "square", label: "Square", hint: "1080 x 1080" },
  { value: "landscape", label: "Landscape", hint: "1200 x 630" },
];

const VARIANTS: { value: Variant; label: string; hint: string }[] = [
  { value: "canvas", label: "Atlas canvas", hint: "Ready to post on its own" },
  { value: "transparent", label: "Transparent", hint: "White text, for dark photos" },
  { value: "light", label: "Light", hint: "Dark text, for pale photos" },
  { value: "map", label: "Map", hint: "Route over the map tiles" },
];

const CHECKER =
  "repeating-conic-gradient(rgba(255,255,255,0.08) 0% 25%, rgba(255,255,255,0.02) 0% 50%) 50% / 20px 20px";
// Light overlay previews on a pale checker so the dark ink is readable.
const CHECKER_LIGHT =
  "repeating-conic-gradient(#E4E9EE 0% 25%, #F6F8FA 0% 50%) 50% / 20px 20px";
const OVERLAY_VARIANTS = new Set<Variant>(["transparent", "light"]);

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "activity";
}

function downloadBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

async function shareOrDownload(blob: Blob, filename: string, text: string, url?: string): Promise<"shared" | "downloaded"> {
  const file = new File([blob], filename, { type: "image/png" });
  const nav = typeof navigator !== "undefined" ? navigator : null;
  if (nav?.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share(url ? { files: [file], text, url } : { files: [file], text });
      return "shared";
    } catch (err) {
      // User dismissed the sheet - not an error worth surfacing.
      if ((err as { name?: string }).name === "AbortError") return "shared";
    }
  }
  downloadBlob(blob, filename);
  return "downloaded";
}

// ─── Map composite ────────────────────────────────────────────────────────────
// A full-size (image px) MapLibre map is mounted off-screen with a 1:1 pixel
// ratio and preserveDrawingBuffer; once idle its canvas is drawn under the
// stats-only transparent overlay from the API. The result IS the download, so
// the preview can never drift from the file.

function useMapComposite(opts: {
  enabled: boolean;
  data: ActivityShareData | undefined;
  format: ActivityShareFormat;
  overlayUrl: string | null;
}) {
  const { enabled, data, format, overlayUrl } = opts;
  const [result, setResult] = React.useState<{ key: string; blob: Blob; previewUrl: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const key = `${format}|${overlayUrl ?? ""}|${data?.activity.id ?? ""}`;
  const layout = ACTIVITY_SHARE_LAYOUT[format];

  React.useEffect(() => {
    return () => {
      if (result?.previewUrl) URL.revokeObjectURL(result.previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.previewUrl]);

  const ready = result?.key === key ? result : null;
  const shouldRender = enabled && !!data && !!overlayUrl && !ready;

  const handleIdle = React.useCallback(
    (map: maplibregl.Map) => {
      if (!overlayUrl) return;
      const overlay = new Image();
      // use-credentials: Strava-sourced images are served to the signed-in
      // owner only (Strava API Policy 6.1); the API allows credentialed CORS.
      overlay.crossOrigin = "use-credentials";
      overlay.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = layout.width;
          canvas.height = layout.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("no 2d context");
          ctx.drawImage(map.getCanvas(), 0, 0, layout.width, layout.height);
          ctx.drawImage(overlay, 0, 0, layout.width, layout.height);
          canvas.toBlob((blob) => {
            if (!blob) {
              setError("Couldn't render the map image");
              return;
            }
            setResult({ key, blob, previewUrl: URL.createObjectURL(blob) });
            setError(null);
          }, "image/png");
        } catch {
          setError("Couldn't render the map image");
        }
      };
      overlay.onerror = () => setError("Couldn't load the overlay");
      overlay.src = overlayUrl;
    },
    [overlayUrl, layout.width, layout.height, key],
  );

  const inset = Math.round(Math.min(layout.routeBox.width, layout.routeBox.height) * 0.06);
  const padding = {
    top: layout.routeBox.y + inset,
    left: layout.routeBox.x + inset,
    right: layout.width - layout.routeBox.x - layout.routeBox.width + inset,
    bottom: layout.height - layout.routeBox.y - layout.routeBox.height + inset,
  };

  const element =
    shouldRender && data ? (
      <div
        aria-hidden
        style={{
          position: "fixed",
          left: -100000,
          top: 0,
          width: layout.width,
          height: layout.height,
          pointerEvents: "none",
        }}
      >
        <ActivityRouteMap
          key={key}
          data={data}
          padding={padding}
          interactive={false}
          preserveDrawingBuffer
          pixelRatio={1}
          onIdle={handleIdle}
        />
      </div>
    ) : null;

  return { element, ready, error, rendering: shouldRender };
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

interface ShareActivityDialogProps {
  activityId: string;
  activityName: string;
  onClose: () => void;
}

export function ShareActivityDialog({ activityId, activityName, onClose }: ShareActivityDialogProps) {
  const [identity, setIdentity] = React.useState(false);
  const [format, setFormat] = React.useState<ActivityShareFormat>("story");
  const [variant, setVariant] = React.useState<Variant>("canvas");
  const [copied, setCopied] = React.useState(false);
  const [busy, setBusy] = React.useState<"idle" | "saving" | "error">("idle");
  const [imgLoaded, setImgLoaded] = React.useState(false);

  const linkQuery = useQuery({
    queryKey: ["activity-share-url", activityId, identity],
    queryFn: () => fetchActivityShareUrl(activityId, identity),
    staleTime: 5 * 60_000,
  });
  const link = linkQuery.data;
  const sig = link ? new URL(link.imageUrl).searchParams.get("sig") : null;
  // Strava-sourced activities have no public link (Strava API Policy 6.1):
  // the dialog becomes image-only.
  const linkable = !!link?.url;

  const dataQuery = useQuery({
    queryKey: ["activity-share", activityId, sig, identity],
    queryFn: () => fetchActivityShare(activityId, sig!, identity),
    enabled: variant === "map" && !!sig,
    staleTime: 5 * 60_000,
  });

  const serverImageUrl =
    link && variant !== "map"
      ? activityShareImageUrl(link.imageUrl, {
          format,
          style: variant === "canvas" ? "brand" : variant === "light" ? "light" : "transparent",
        })
      : null;
  const overlayUrl = link
    ? activityShareImageUrl(link.imageUrl, { format, style: "transparent", route: false })
    : null;

  const composite = useMapComposite({
    enabled: variant === "map",
    data: dataQuery.data,
    format,
    overlayUrl,
  });

  React.useEffect(() => setImgLoaded(false), [serverImageUrl]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const layout = ACTIVITY_SHARE_LAYOUT[format];
  const filename = `atlas-${slugify(activityName)}-${format}${variant === "transparent" ? "-overlay" : variant === "light" ? "-overlay-light" : ""}.png`;

  async function currentBlob(): Promise<Blob> {
    if (variant === "map") {
      if (!composite.ready) throw new Error("not ready");
      return composite.ready.blob;
    }
    if (!serverImageUrl) throw new Error("no image");
    const res = await fetch(serverImageUrl, { credentials: "include" });
    if (!res.ok) throw new Error("fetch failed");
    return res.blob();
  }

  async function handleSave(mode: "download" | "share") {
    if (!link) return;
    setBusy("saving");
    try {
      const blob = await currentBlob();
      if (mode === "share") {
        await shareOrDownload(blob, filename, activityName, link.url ?? undefined);
      } else {
        downloadBlob(blob, filename);
      }
      setBusy("idle");
    } catch {
      setBusy("error");
      setTimeout(() => setBusy("idle"), 2000);
    }
  }

  async function handleCopy() {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked - the URL is still visible in the field */
    }
  }

  const canShareFiles =
    typeof navigator !== "undefined" && !!navigator.share && typeof navigator.canShare === "function";
  const previewReady = variant === "map" ? !!composite.ready : imgLoaded;
  const previewSrc = variant === "map" ? composite.ready?.previewUrl ?? null : serverImageUrl;
  const previewError = variant === "map" ? composite.error ?? (dataQuery.isError ? "Couldn't load the route" : null) : null;

  const segStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    padding: "8px 12px",
    borderRadius: "var(--radius-md)",
    border: `1px solid ${active ? "var(--border-gold)" : "var(--border-default)"}`,
    background: active ? "var(--accent-soft, rgba(244,183,64,0.08))" : "var(--surface-sunken)",
    color: "var(--text-primary)",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    flex: 1,
    minWidth: 0,
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Share activity"
    >
      {composite.element}
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 24,
          maxWidth: 860,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          boxShadow: "var(--shadow-xl)",
          minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Share activity</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
              A public link anyone can open, plus images sized for social. The link is unguessable and can&#39;t be
              revoked, so share it deliberately.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: 0, color: "var(--text-secondary)", cursor: "pointer", padding: 4, flexShrink: 0 }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 320px) minmax(0, 1fr)", gap: 20 }}>
          {/* Preview */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: `${layout.width} / ${layout.height}`,
                maxHeight: 460,
                margin: "0 auto",
                borderRadius: "var(--radius-lg)",
                overflow: "hidden",
                border: "1px solid var(--border-subtle)",
                background: variant === "light" ? CHECKER_LIGHT : variant === "transparent" ? CHECKER : "var(--surface-sunken)",
              }}
            >
              {previewSrc && (
                <img
                  key={previewSrc}
                  src={previewSrc}
                  alt={`${activityName} share image preview`}
                  crossOrigin="use-credentials"
                  onLoad={() => setImgLoaded(true)}
                  onError={() => setBusy("error")}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: previewReady ? 1 : 0,
                    transition: "opacity .2s",
                  }}
                />
              )}
              {!previewReady && !previewError && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    color: "var(--text-muted)",
                  }}
                >
                  {variant === "map" ? "Rendering map…" : "Rendering…"}
                </div>
              )}
              {previewError && (
                <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 13, color: "var(--status-danger)", padding: 12, textAlign: "center" }}>
                  {previewError}
                </div>
              )}
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
              {layout.width} x {layout.height}
              {OVERLAY_VARIANTS.has(variant) ? " · transparent background" : ""}
            </p>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Format</div>
              <div style={{ display: "flex", gap: 8 }} className="r-wrap">
                {FORMATS.map((f) => (
                  <button key={f.value} type="button" style={segStyle(format === f.value)} onClick={() => setFormat(f.value)}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{f.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Look</div>
              <div style={{ display: "flex", gap: 8 }} className="r-wrap">
                {VARIANTS.map((v) => (
                  <button key={v.value} type="button" style={segStyle(variant === v.value)} onClick={() => setVariant(v.value)}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{v.label}</span>
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{v.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={identity}
                onChange={(e) => setIdentity(e.target.checked)}
                style={{ marginTop: 3, accentColor: "var(--gold-500)" }}
              />
              <span style={{ fontSize: 13.5, color: "var(--text-primary)", lineHeight: 1.45 }}>
                Show my name, avatar and level
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
                  Off by default. Atlas branding always shows.
                </span>
              </span>
            </label>

            {link && !linkable ? (
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.45 }}>
                Strava activities can be shared as an image only. Public links are not
                available for data imported from Strava.
              </div>
            ) : (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Public link</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                <input
                  readOnly
                  value={link?.url ?? (linkQuery.isError ? "Couldn't create link" : "Creating link…")}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: "var(--control-sm)",
                    padding: "0 10px",
                    background: "var(--surface-sunken)",
                    border: "1px solid var(--border-default)",
                    borderRadius: "var(--radius-md)",
                    color: "var(--text-secondary)",
                    fontSize: 12.5,
                    fontFamily: "var(--font-mono)",
                  }}
                />
                <Button variant="secondary" size="sm" leftIcon={copied ? <Check size={13} /> : <Copy size={13} />} disabled={!link} onClick={() => void handleCopy()}>
                  {copied ? "Copied" : "Copy"}
                </Button>
                {link?.pageUrl && (
                  <a href={link.pageUrl} target="_blank" rel="noreferrer" title="Open the public page" style={{ display: "inline-flex", color: "var(--text-secondary)" }}>
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: "auto" }}>
              <Button
                leftIcon={<Download size={14} />}
                disabled={!link || !previewReady || busy === "saving"}
                onClick={() => void handleSave("download")}
              >
                {busy === "saving" ? "Saving…" : busy === "error" ? "Couldn't save" : "Download image"}
              </Button>
              {canShareFiles && (
                <Button
                  variant="secondary"
                  leftIcon={<Share2 size={14} />}
                  disabled={!link || !previewReady || busy === "saving"}
                  onClick={() => void handleSave("share")}
                >
                  Share…
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
